import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { RequirePermission } from '../components/NoAccess';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type {
  ActiveRestriction, ComplianceSummary, CoverageState, CursorPage, OutstandingRow, Plant,
} from '@dru/types';
import { BRAND, COVERAGE_LABEL } from '@dru/types';
import {
  Alert, Button, DataTable, FilterBar, IconAction, InfoTip, KeyFigures, RowActions, SearchInput, StatusBadge,
  Tabs, cn, coverageTone, useToast, type SortState,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { formatDate } from '../../../lib/format';
import { useQuery } from '../../../lib/useQuery';
import { usePages } from '../../../lib/usePages';
import { useAuth } from '../../../lib/auth';
import { PageHeader, holds } from '../../../layouts/ConsoleShell';
import { ArrangeDialog } from '../components/ArrangeDialog';
import { CalendarPlus, Eye, Repeat } from 'lucide-react';
import { CASE_STATUS_LABEL, NEXT_ACTION_ICON, NEXT_ACTION_LABEL, caseStatusTone, nextCaseAction } from './Examinations';

/**
 * The worklist HR actually works from: who is outstanding, and the single next
 * action for each. A report tells you the number; this tells you what to do.
 *
 * Two views of one subject, on their existing addresses: the overview
 * (/hr/compliance, who is not covered) and upcoming renewals
 * (/hr/compliance/due, certificates by the date they run out). Both open on
 * the same six counts, which come from /compliance/summary — never from the
 * rows on screen, which are paged.
 *
 * Rows are sorted by urgency — overdue, never examined, missed appointment,
 * retest, then due soon — because a list that opens on "In progress" hides
 * the worker who has no cover at all three screens down. The server pages by
 * employee, not by state, so the page fetches the following pages itself
 * (up to a cap) before the order can be trusted, and says so when it stops
 * short.
 *
 * A worker the physician found unfit or temporarily unfit is on this list as
 * exactly that, never as "never examined".
 */

/**
 * Two worklists, not one: what is HR's to do, and what is waiting on the lab
 * or the physician. A number that is mostly somebody else's work stops being
 * read.
 */
type Tab = 'outstanding' | 'waiting' | 'restrictions';

/** The company's to act on. An older API sends no `needsCompany`: then a case that is open is waited on. */
const isMine = (r: OutstandingRow) => r.needsCompany ?? r.state !== 'IN_PROGRESS';
const isMissed = (r: OutstandingRow) => r.openOrderStatus === 'NO_SHOW_OPEN';

/** Most urgent first: overdue → never examined → missed → retest → the rest → due soon. */
function priority(r: OutstandingRow): number {
  if (r.state === 'OVERDUE') return 0;
  if (r.state === 'NEVER_EXAMINED') return 1;
  if (isMissed(r)) return 2;
  if (r.state === 'TEMPORARILY_UNFIT' || r.state === 'FURTHER_EVALUATION') return 3;
  if (r.state === 'IN_PROGRESS' && isMine(r)) return 4;
  if (r.state === 'UNFIT') return 5;
  if (r.state === 'DUE_SOON') return 6;
  return 7;
}

/** The two "examined, not cleared" states, filtered together: one question for HR, two badges on the rows. */
type StateFilter = CoverageState | 'NOT_CLEARED' | 'MISSED';
const NOT_CLEARED: CoverageState[] = ['UNFIT', 'TEMPORARILY_UNFIT', 'FURTHER_EVALUATION'];

/** In the order the list is sorted. */
const STATE_FILTER: Array<{ value: StateFilter; label: string }> = [
  { value: 'OVERDUE', label: 'Expired (certificate past its date)' },
  { value: 'NEVER_EXAMINED', label: COVERAGE_LABEL.NEVER_EXAMINED },
  { value: 'MISSED', label: 'Missed appointment' },
  { value: 'NOT_CLEARED', label: 'Not cleared / retest' },
  { value: 'DUE_SOON', label: COVERAGE_LABEL.DUE_SOON },
  { value: 'IN_PROGRESS', label: 'Case open — your move' },
];

const inState = (row: OutstandingRow, filter: StateFilter | '') =>
  !filter || row.state === filter
  || (filter === 'NOT_CLEARED' && NOT_CLEARED.includes(row.state))
  || (filter === 'MISSED' && isMissed(row));

const today = () => new Date().toLocaleDateString('en-CA');
/** A retest date that has gone by with nothing raised for it. */
const retestOverdue = (r: OutstandingRow) =>
  r.state === 'TEMPORARILY_UNFIT' && !!r.retestDueOn && r.retestDueOn < today();

/**
 * The renewal window, in the words HR plans in. Each maps onto the API's
 * own `dueWithinDays`, so the "Due soon" count and the rows agree.
 */
type Window = 'today' | 'week' | 'month' | '30' | '60' | '90';
const WINDOWS: Array<{ value: Window; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
];
function windowDays(w: Window): number {
  const now = new Date();
  if (w === 'today') return 0;
  // To the coming Sunday, the end of the working week's calendar week.
  if (w === 'week') return (7 - now.getDay()) % 7;
  if (w === 'month') return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate() - now.getDate();
  return Number(w);
}
const windowPhrase = (w: Window) =>
  w === 'today' ? 'today' : w === 'week' ? 'this week' : w === 'month' ? 'this month' : `within ${w} days`;

/** Pages fetched on their own before the urgency order is trusted. 200 employees each. */
const AUTO_PAGES = 10;

/**
 * The top bar's plant switcher scopes every read for a company-wide HR
 * login. A page filter beside it was the same question asked twice; it is
 * shown only to a login that has no switcher and more than one plant.
 */
export function usePagePlantFilter() {
  const { principal, permissions } = useAuth();
  const companyWide = !!principal?.scopes?.some((x) => x.scopeType === 'TENANT')
    && !principal?.scopes?.some((x) => x.scopeType === 'PLANT');
  const hasSwitcher = companyWide && holds(permissions, 'plant.read');
  const plants = useQuery<CursorPage<Plant>>(
    () => api.get<CursorPage<Plant>>('/plants?limit=100'), []);
  const options = (plants.data?.items ?? []).map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }));
  return {
    /** Show a Plant filter on the page. */
    filter: !hasSwitcher && options.length > 1,
    options,
    /** Show a Plant column: more than one plant and no single plant in view. */
    column: options.length > 1 && !api.getViewPlant(),
  };
}

/** The two views, as one control over their existing addresses. */
export function ComplianceViews({ active }: { active: 'overview' | 'due' }) {
  const item = (key: 'overview' | 'due', to: string, label: string) => (
    <Link
      to={to}
      aria-current={active === key ? 'page' : undefined}
      className={cn(
        'inline-flex min-h-9 flex-1 items-center justify-center rounded-btn px-4 text-small font-semibold transition-colors max-md:min-h-10 sm:flex-none',
        active === key
          ? 'bg-white text-primary-700 shadow-card'
          : 'text-neutral-600 hover:text-neutral-900',
      )}
    >
      {label}
    </Link>
  );
  return (
    <nav aria-label="Compliance views" className="inline-flex w-full gap-1 rounded-card bg-neutral-100 p-1 sm:w-auto">
      {item('overview', '/hr/compliance', 'Compliance overview')}
      {item('due', '/hr/compliance/due', 'Upcoming renewals')}
    </nav>
  );
}

/**
 * The six counts both views open on, from /compliance/summary. A tile that
 * names a group opens the overview filtered to it.
 */
export function CoverageFigures({ summary, loading, dueLabel, onPick }: {
  summary?: ComplianceSummary | null;
  loading: boolean;
  dueLabel: string;
  onPick: (to: 'employees' | 'covered' | StateFilter) => void;
}) {
  const s = summary;
  // `overdue` is everybody without cover; the tile counts the lapsed only,
  // since never examined and not cleared have tiles of their own.
  const expired = s ? s.lapsed ?? Math.max(s.overdue - s.neverExamined - (s.notCleared ?? 0), 0) : 0;
  const clear = (n: number, hint: string) => (n ? hint : 'None — all clear');
  return (
    <KeyFigures
      label="Coverage"
      figures={[
        {
          key: 'total', label: 'Total employees', value: s?.totalEmployees ?? 0,
          hint: 'Active workforce', loading, onClick: () => onPick('employees'),
        },
        {
          key: 'covered', label: 'Covered', value: s?.covered ?? 0,
          hint: s ? `${s.coveragePercent}% have a valid certificate` : undefined,
          tone: s?.covered ? 'covered' : undefined, loading, onClick: () => onPick('covered'),
        },
        {
          key: 'overdue', label: 'Expired', value: expired,
          hint: clear(expired, 'Certificate expired'), tone: expired ? 'overdue' : undefined,
          loading, onClick: () => onPick('OVERDUE'),
        },
        {
          key: 'due', label: 'Due soon', value: s?.dueSoon ?? 0,
          hint: clear(s?.dueSoon ?? 0, `Expires ${dueLabel}`), tone: s?.dueSoon ? 'dueSoon' : undefined,
          loading, onClick: () => onPick('DUE_SOON'),
        },
        {
          key: 'never', label: 'Never examined', value: s?.neverExamined ?? 0,
          hint: clear(s?.neverExamined ?? 0, 'No checkup on record'), tone: s?.neverExamined ? 'overdue' : undefined,
          loading, onClick: () => onPick('NEVER_EXAMINED'),
        },
        {
          key: 'notCleared', label: 'Not cleared', value: s?.notCleared ?? 0,
          hint: s?.notCleared
            ? `${s.unfit} unfit · ${s.temporarilyUnfit} temporary${s.furtherEvaluation ? ` · ${s.furtherEvaluation} further evaluation` : ''}`
            : 'None — all clear',
          tone: s?.notCleared ? 'overdue' : undefined, loading, onClick: () => onPick('NOT_CLEARED'),
        },
      ]}
    />
  );
}

function CompliancePage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tabParam = params.get('tab');
  const tab: Tab = tabParam === 'restrictions' || tabParam === 'waiting' ? tabParam : 'outstanding';
  const state = (params.get('state') ?? '') as StateFilter | '';
  const plantId = params.get('plant') ?? '';
  const winParam = params.get('within') as Window | null;
  const win: Window = WINDOWS.some((w) => w.value === winParam) ? winParam! : '30';
  const days = windowDays(win);
  const [search, setSearch] = useState('');
  // Urgency by default; a column heading re-sorts what is loaded.
  const [sort, setSort] = useState<SortState>({ key: 'priority', direction: 'asc' });
  const { permissions } = useAuth();
  const { toast } = useToast();
  const [arranging, setArranging] = useState<OutstandingRow | null>(null);
  const canArrange = holds(permissions, 'examination.create');
  const canOpenCase = holds(permissions, 'examination.read');
  const plant = usePagePlantFilter();
  /** What this login may do to the row's open case — the rule the case screen itself applies. */
  const caseAction = (r: OutstandingRow) =>
    (r.openOrderStatus ? nextCaseAction(r.openOrderStatus, permissions) : null);

  /** The one thing to do for a row, as a labelled button, or nothing this login may do. */
  const rowAction = (r: OutstandingRow): ReactNode => {
    if (r.state !== 'IN_PROGRESS') {
      if (!canArrange) return null;
      const retest = r.state === 'TEMPORARILY_UNFIT' || r.state === 'FURTHER_EVALUATION';
      return (
        <Button size="sm" variant="primary"
                icon={retest ? <Repeat className="h-4 w-4" /> : <CalendarPlus className="h-4 w-4" />}
                aria-label={`${retest ? 'Arrange retest' : r.state === 'UNFIT' ? 'Arrange re-examination' : 'Schedule checkup'} for ${r.fullName}`}
                onClick={(e) => { e.stopPropagation(); setArranging(r); }}>
          {retest ? 'Retest' : r.state === 'UNFIT' ? 'Re-examine' : r.state === 'DUE_SOON' ? 'Schedule renewal' : 'Schedule checkup'}
        </Button>
      );
    }
    if (!(r.openOrderId && canOpenCase)) return null;
    const act = caseAction(r);
    // A missed slot on a worker a certificate already covers is a decision —
    // cancel or rebook — so it opens the case rather than offering "Rebook".
    if (act && !(isMissed(r) && r.validUntil)) {
      const Icon = NEXT_ACTION_ICON[act];
      return (
        <Button size="sm" variant="primary" icon={<Icon className="h-4 w-4" />}
                aria-label={`Open the medical case of ${r.fullName} to ${NEXT_ACTION_LABEL[act].toLowerCase()}`}
                onClick={(e) => { e.stopPropagation(); navigate(`/hr/examinations/${r.openOrderId}`); }}>
          {/* It opens the case, where the action is taken; the label says so. */}
          Open case to {NEXT_ACTION_LABEL[act].toLowerCase()}
        </Button>
      );
    }
    return (
      <Button size="sm" variant="secondary" icon={<Eye className="h-4 w-4" />}
              aria-label={`Open medical case for ${r.fullName}`}
              onClick={(e) => { e.stopPropagation(); navigate(`/hr/examinations/${r.openOrderId}`); }}>
        View case
      </Button>
    );
  };

  const plantQs = plantId ? `&plantId=${plantId}` : '';

  const summary = useQuery<ComplianceSummary>(
    () => api.get(`/compliance/summary?dueWithinDays=${days}${plantQs}`),
    [days, plantId],
  );

  const outstanding = usePages<OutstandingRow>(
    (cursor) => api.get<{ rows: OutstandingRow[]; nextCursor: string | null }>(
      `/compliance/outstanding?limit=200&dueWithinDays=${days}${plantQs}${cursor ? `&cursor=${cursor}` : ''}`,
    ).then((p) => ({ items: p.rows, nextCursor: p.nextCursor })),
    [days, plantId],
  );

  // The server pages by employee, so the most urgent worker may be on a page
  // not yet fetched. Fetch on, up to a cap, before sorting means anything.
  const autoPages = useRef(0);
  useEffect(() => { if (outstanding.loading) autoPages.current = 0; }, [outstanding.loading]);
  useEffect(() => {
    if (outstanding.loading || outstanding.loadingMore || outstanding.error || !outstanding.hasMore) return;
    if (autoPages.current >= AUTO_PAGES) return;
    autoPages.current += 1;
    void outstanding.loadMore();
  }, [outstanding.loading, outstanding.loadingMore, outstanding.error, outstanding.hasMore, outstanding.loadMore]);
  const partial = !outstanding.loading && outstanding.hasMore && autoPages.current >= AUTO_PAGES;

  const restrictions = useQuery<ActiveRestriction[]>(
    () => api.get(`/compliance/restrictions?limit=200${plantQs}`),
    [plantId],
  );

  const needle = search.trim().toLowerCase();
  const matches = (r: { fullName: string; employeeCode: string }) =>
    !needle || r.fullName.toLowerCase().includes(needle) || r.employeeCode.toLowerCase().includes(needle);

  const mine = useMemo(() => outstanding.items.filter(isMine), [outstanding.items]);
  const waiting = useMemo(() => outstanding.items.filter((r) => !isMine(r)), [outstanding.items]);

  const rows = useMemo(() => {
    const flip = sort.direction === 'asc' ? 1 : -1;
    const byUrgency = (a: OutstandingRow, b: OutstandingRow) => priority(a) - priority(b)
      || (a.validUntil ?? '').localeCompare(b.validUntil ?? '')
      || a.fullName.localeCompare(b.fullName);
    return (tab === 'waiting' ? waiting : mine)
      // The state filter belongs to the first list; the second is one state.
      .filter((r) => (tab === 'waiting' || inState(r, state)) && matches(r))
      .sort((a, b) => (sort.key === 'name'
        ? flip * a.fullName.localeCompare(b.fullName)
        : flip * byUrgency(a, b)));
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [mine, waiting, tab, state, needle, sort]);

  const restrictionRows = (restrictions.data ?? []).filter(matches);
  const counting = outstanding.loading || outstanding.loadingMore;

  const setParam = (mut: (p: URLSearchParams) => void) => {
    const p = new URLSearchParams(params);
    mut(p);
    setParams(p, { replace: true });
  };
  const pick = (to: 'employees' | 'covered' | StateFilter) => {
    if (to === 'employees') { navigate('/hr/employees'); return; }
    if (to === 'covered') { navigate('/hr/certificates'); return; }
    setParam((p) => { p.delete('tab'); if (state === to) p.delete('state'); else p.set('state', to); });
  };
  const openTab = (next: Tab) => setParam((p) => {
    if (next === 'outstanding') p.delete('tab'); else p.set('tab', next);
    // The state filter is the first list's; it must not hide the other two.
    if (next !== 'outstanding') p.delete('state');
  });

  return (
    <div className="space-y-4">
      <PageHeader
        className="!mb-0"
        title="Compliance"
        hint="Who is not covered, and the next step for each. Dates and fitness outcomes only — no clinical findings."
      />

      <ComplianceViews active="overview" />

      <CoverageFigures summary={summary.data} loading={summary.loading} dueLabel={windowPhrase(win)} onPick={pick} />

      <Tabs
        active={tab}
        onChange={(k) => openTab(k as Tab)}
        tabs={[
          { key: 'outstanding', label: 'Needs HR action', hint: counting ? undefined : mine.length },
          { key: 'waiting', label: 'Waiting for lab / physician', hint: counting ? undefined : waiting.length },
          { key: 'restrictions', label: 'Restrictions', hint: restrictions.data?.length },
        ]}
      />

      <FilterBar
        filters={[
          ...(tab === 'outstanding' ? [{
            key: 'state', label: 'Reason', allLabel: 'Any reason',
            options: STATE_FILTER,
          }] : []),
          ...(tab === 'outstanding' ? [{
            key: 'within', label: 'Due', allLabel: 'Due within 30 days',
            options: WINDOWS.filter((w) => w.value !== '30').map((w) => ({
              value: w.value, label: w.value === 'today' ? 'Due today' : w.value === 'week' || w.value === 'month' ? `Due ${w.label.toLowerCase()}` : `Due within ${w.label}`,
            })),
          }] : []),
          ...(plant.filter ? [{
            key: 'plant', label: 'Plant', allLabel: 'All plants', options: plant.options,
          }] : []),
        ]}
        value={{
          state: tab === 'outstanding' ? state || undefined : undefined,
          within: tab === 'outstanding' && win !== '30' ? win : undefined,
          plant: plantId || undefined,
        }}
        onChange={(v) => setParam((p) => {
          for (const k of ['state', 'plant', 'within'] as const) {
            if (v[k]) p.set(k, v[k]!); else p.delete(k);
          }
        })}
      >
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search name or employee ID"
          aria-label="Search by name or employee ID"
          className="w-full sm:w-64"
        />
      </FilterBar>

      {tab === 'waiting' && (
        <p className="text-small text-neutral-600">
          Nothing to do here yet: these cases are with {BRAND.name} Operations, the venue, the laboratory or the physician.
          They move back to <button type="button" className="font-medium text-primary-700 hover:underline" onClick={() => openTab('outstanding')}>Needs HR action</button> if they need you.
        </p>
      )}

      {partial && tab !== 'restrictions' && (
        <Alert tone="info" title="Sorted among the employees checked so far">
          Your company is large, so the list stops after the first {AUTO_PAGES * 200} employees.
          Select “Load more” at the bottom to check the rest. The counts above cover everyone.
        </Alert>
      )}

      {tab !== 'restrictions' ? (
        <DataTable<OutstandingRow>
          rows={rows}
          sort={sort}
          onSortChange={setSort}
          loading={outstanding.loading}
          error={outstanding.error}
          onRetry={outstanding.reload}
          onLoadMore={outstanding.loadMore}
          hasMore={outstanding.hasMore}
          loadingMore={outstanding.loadingMore}
          rowKey={(r) => r.employeeId}
          onRowClick={(r) => navigate(`/hr/employees/${r.employeeId}`)}
          empty={(tab === 'outstanding' && state) || needle ? {
            title: 'Nobody matches these filters',
            hint: 'Clear the search or the reason filter to see everyone on this list.',
          } : tab === 'waiting' ? {
            title: 'Nothing is waiting on the lab or the physician',
            hint: 'Open cases with Operations, the venue, the laboratory or the physician appear here.',
          } : {
            title: 'Nothing needs your action',
            hint: waiting.length
              ? `Nobody is overdue, unexamined, unfit or due ${windowPhrase(win)}. ${waiting.length} open case${waiting.length === 1 ? ' is' : 's are'} with the lab or physician.`
              : `No employee is overdue, unexamined, found unfit, or due ${windowPhrase(win)}.`,
          }}
          columns={[
            {
              key: 'code', header: 'Employee ID',
              cell: (r) => <span className="whitespace-nowrap font-mono text-small text-neutral-700">{r.employeeCode}</span>,
            },
            {
              key: 'employee', header: 'Name', alwaysVisible: true, sortKey: 'name',
              cell: (r) => (
                <Link
                  to={`/hr/employees/${r.employeeId}`}
                  onClick={(e) => e.stopPropagation()}
                  className="font-medium text-neutral-900 hover:text-primary-700"
                >
                  {r.fullName}
                </Link>
              ),
            },
            ...(plant.column ? [{
              key: 'plant', header: 'Plant', className: 'max-lg:hidden',
              cell: (r: OutstandingRow) => <span className="text-small text-neutral-700">{r.plant ?? '—'}</span>,
            }] : []),
            {
              key: 'state', header: 'Status', sortKey: 'priority',
              cell: (r) => (
                // A case that is open says where it has got to, in the words
                // and colours of the case list.
                r.state === 'IN_PROGRESS' && r.openOrderStatus ? (
                  <StatusBadge tone={caseStatusTone(r.openOrderStatus)}>
                    {isMissed(r) ? 'Missed appointment' : CASE_STATUS_LABEL[r.openOrderStatus] ?? r.openOrderStatus}
                  </StatusBadge>
                ) : (
                  <StatusBadge tone={coverageTone(r.state)}>
                    {r.state === 'OVERDUE' ? 'Expired' : COVERAGE_LABEL[r.state] ?? r.state}
                  </StatusBadge>
                )
              ),
            },
            {
              key: 'due', header: 'Due date',
              cell: (r) => <DueCell row={r} />,
            },
            {
              key: 'action', header: 'Next action',
              // The API writes the sentence, so the worklist and the export
              // say the same thing, and it already names who has a waiting case.
              cell: (r) => (
                <div className="flex flex-col items-start gap-1.5 md:min-w-[16rem] lg:flex-row lg:items-center lg:justify-between lg:gap-4">
                  <span className={cn('text-small', retestOverdue(r) ? 'font-medium text-overdue-600' : 'text-neutral-700')}>
                    {r.action}
                  </span>
                  <RowActions className="shrink-0 justify-start">
                    {rowAction(r)}
                    <IconAction label={`View ${r.fullName}`} icon={Eye}
                                onClick={() => navigate(`/hr/employees/${r.employeeId}`)} />
                  </RowActions>
                </div>
              ),
            },
          ]}
        />
      ) : (
        <>
          <p className="flex items-center gap-1 text-small text-neutral-500">
            Restrictions in force are instructions to the employer.
            <InfoTip label="About restrictions">
              The code and its public label appear here. The clinical reason behind a restriction
              does not.
            </InfoTip>
          </p>
          <DataTable<ActiveRestriction>
            rows={restrictionRows}
            loading={restrictions.loading}
            error={restrictions.error}
            onRetry={restrictions.reload}
            rowKey={(r) => `${r.employeeCode}-${r.restrictionCode}`}
            empty={needle ? {
              title: 'Nobody matches this search',
              hint: 'Clear the search to see every restriction in force.',
            } : {
              title: 'No active restrictions',
              hint: 'No current certificate carries a work restriction.',
            }}
            columns={[
              {
                key: 'employee', header: 'Employee', alwaysVisible: true,
                cell: (r) => (
                  <div>
                    <div className="font-medium text-neutral-900">{r.fullName}</div>
                    <div className="text-caption text-neutral-500">
                      <span className="font-mono">{r.employeeCode}</span>
                      {r.plant && <> · {r.plant}</>}
                    </div>
                  </div>
                ),
              },
              {
                key: 'restriction', header: 'Restriction',
                cell: (r) => (
                  <div>
                    <StatusBadge tone="restricted">{r.restrictionCode}</StatusBadge>
                    <div className="mt-0.5 text-small text-neutral-700">{r.restriction}</div>
                  </div>
                ),
              },
              {
                key: 'until', header: 'In force until', align: 'right',
                cell: (r) => <span className="text-small tabular-nums text-neutral-600">{formatDate(r.validUntil)}</span>,
              },
            ]}
          />
        </>
      )}

      {arranging && (
        <ArrangeDialog
          employee={{ id: arranging.employeeId, fullName: arranging.fullName, employeeCode: arranging.employeeCode }}
          onClose={() => setArranging(null)}
          onDone={() => {
            // Said out loud: the dialog closed and the row merely changed
            // state, which read as nothing having happened.
            toast('success', `Checkup arranged for ${arranging.fullName}. It now waits for a lab and a slot.`);
            setArranging(null); outstanding.reload(); summary.reload();
          }}
        />
      )}
    </div>
  );
}

/** The date that matters for the row, and how it stands against today. */
function DueCell({ row: r }: { row: OutstandingRow }) {
  const line = (date: string | null | undefined, lead: string, urgent?: boolean) => (date ? (
    <div className="whitespace-nowrap">
      <div className={cn('tabular-nums text-small', urgent ? 'font-medium text-overdue-600' : 'text-neutral-800')}>{formatDate(date)}</div>
      <div className="text-caption text-neutral-500">{lead}</div>
    </div>
  ) : <span className="text-small text-neutral-500">—</span>);
  if (r.state === 'NEVER_EXAMINED') return <span className="text-small font-medium text-overdue-600">Due now</span>;
  if (isMissed(r)) return <span className="whitespace-nowrap text-small font-medium text-overdue-600">Rebook now</span>;
  if (r.state === 'OVERDUE') return line(r.validUntil, 'Expired', true);
  if (r.state === 'TEMPORARILY_UNFIT' && r.retestDueOn) return line(r.retestDueOn, 'Retest by', retestOverdue(r));
  if (r.state === 'IN_PROGRESS' && r.bookedFor) return line(r.bookedFor, 'Booked for');
  if (r.validUntil) return line(r.validUntil, r.state === 'DUE_SOON' ? 'Expires' : 'Valid until');
  if (r.examinedOn) return line(r.examinedOn, 'Examined');
  return <span className="text-small text-neutral-500">—</span>;
}

export function Compliance() {
  return <RequirePermission need="compliance.read" title="Compliance" what="Compliance"><CompliancePage /></RequirePermission>;
}
